/**
 * Templates Page
 * Manage execution templates for coder agents
 */

import { useState, useEffect } from 'react';
import {
  FileCode,
  RefreshCw,
  ChevronRight,
  Cpu,
  HardDrive,
  Clock,
  Terminal,
  AlertCircle,
  XCircle,
} from 'lucide-react';
import { coderApi, type ExecutionTemplate } from '../api/coder';

export default function TemplatesPage() {
  const [templates, setTemplates] = useState<ExecutionTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<ExecutionTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const result = await coderApi.listTemplates();
      setTemplates(result.templates);
      setError(null);
    } catch (err) {
      console.error('Failed to load templates:', err);
      setError(err instanceof Error ? err.message : 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = async (templateId: string) => {
    try {
      const template = await coderApi.getTemplate(templateId);
      setSelectedTemplate(template);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load template');
    }
  };

  return (
    <div className="flex-1 h-full overflow-hidden flex">
      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b" style={{ borderColor: '#E8E0D5', backgroundColor: 'white' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: '#10B98120', color: '#10B981' }}
              >
                <FileCode className="w-5 h-5" />
              </div>
              <div>
                <h1 className="text-xl font-semibold" style={{ color: '#2F241B' }}>
                  Execution Templates
                </h1>
                <p className="text-sm" style={{ color: '#6B5D52' }}>
                  Pre-configured environments for code execution
                </p>
              </div>
            </div>
            <button
              onClick={loadTemplates}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              title="Refresh"
              aria-label="Refresh templates"
            >
              <RefreshCw className="w-5 h-5" style={{ color: '#6B5D52' }} />
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mx-6 mt-4 p-4 rounded-lg flex items-center gap-3" style={{ backgroundColor: '#FEE2E2' }}>
            <AlertCircle className="w-5 h-5 text-red-500" />
            <span className="text-red-700">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto" aria-label="Dismiss error">
              <XCircle className="w-5 h-5 text-red-500" />
            </button>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 mx-auto mb-4 animate-spin" style={{ color: '#A39686' }} />
              <p style={{ color: '#6B5D52' }}>Loading templates...</p>
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-12">
              <FileCode className="w-12 h-12 mx-auto mb-4" style={{ color: '#A39686' }} />
              <h3 className="text-lg font-medium mb-2" style={{ color: '#2F241B' }}>
                No Templates Available
              </h3>
              <p className="text-sm" style={{ color: '#6B5D52' }}>
                Templates define execution environments for coder agents
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className="bg-white rounded-lg p-6 border cursor-pointer hover:shadow-md transition-all"
                  style={{ borderColor: '#E8E0D5' }}
                  onClick={() => handleSelectTemplate(template.id)}
                >
                  <div className="flex items-start justify-between">
                    <div
                      className="w-12 h-12 rounded-lg flex items-center justify-center"
                      style={{
                        backgroundColor:
                          template.id.includes('react')
                            ? '#3B82F620'
                            : template.id.includes('python')
                            ? '#FBBF2420'
                            : '#10B98120',
                        color:
                          template.id.includes('react')
                            ? '#3B82F6'
                            : template.id.includes('python')
                            ? '#FBBF24'
                            : '#10B981',
                      }}
                    >
                      <FileCode className="w-6 h-6" />
                    </div>
                    <ChevronRight className="w-5 h-5" style={{ color: '#A39686' }} />
                  </div>
                  <h3 className="text-lg font-semibold mt-4" style={{ color: '#2F241B' }}>
                    {template.name}
                  </h3>
                  <p className="text-sm mt-2" style={{ color: '#6B5D52' }}>
                    {template.description}
                  </p>
                  <div className="flex flex-wrap gap-2 mt-4">
                    <span
                      className="px-2 py-1 rounded text-xs font-medium"
                      style={{ backgroundColor: '#F5F1EC', color: '#6B5D52' }}
                    >
                      <Cpu className="w-3 h-3 inline mr-1" />
                      {template.spriteConfig.cpus} CPU
                    </span>
                    <span
                      className="px-2 py-1 rounded text-xs font-medium"
                      style={{ backgroundColor: '#F5F1EC', color: '#6B5D52' }}
                    >
                      <HardDrive className="w-3 h-3 inline mr-1" />
                      {template.spriteConfig.memoryMb}MB
                    </span>
                    <span
                      className="px-2 py-1 rounded text-xs font-medium"
                      style={{ backgroundColor: '#F5F1EC', color: '#6B5D52' }}
                    >
                      <Clock className="w-3 h-3 inline mr-1" />
                      {Math.round(template.spriteConfig.timeout / 60000)}min
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Template Detail Sidebar */}
      {selectedTemplate && (
        <div
          className="w-[500px] border-l overflow-y-auto"
          style={{ borderColor: '#E8E0D5', backgroundColor: '#F5F1ED' }}
        >
          <div className="sticky top-0 bg-white border-b px-6 py-4" style={{ borderColor: '#E8E0D5' }}>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold" style={{ color: '#2F241B' }}>
                {selectedTemplate.name}
              </h2>
              <button
                onClick={() => setSelectedTemplate(null)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Close sidebar"
              >
                <XCircle className="w-5 h-5" style={{ color: '#6B5D52' }} />
              </button>
            </div>
            <p className="text-sm mt-1" style={{ color: '#6B5D52' }}>
              {selectedTemplate.description}
            </p>
          </div>

          <div className="p-6 space-y-6">
            {/* Sprite Configuration */}
            <div className="bg-white rounded-lg p-4 border" style={{ borderColor: '#E8E0D5' }}>
              <h3 className="font-medium mb-4" style={{ color: '#2F241B' }}>
                Sprite Configuration
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm" style={{ color: '#6B5D52' }}>Machine Size</span>
                  <span className="font-medium" style={{ color: '#2F241B' }}>
                    {selectedTemplate.spriteConfig.machineSize}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm" style={{ color: '#6B5D52' }}>Region</span>
                  <span className="font-medium" style={{ color: '#2F241B' }}>
                    {selectedTemplate.spriteConfig.region}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm" style={{ color: '#6B5D52' }}>CPUs</span>
                  <span className="font-medium" style={{ color: '#2F241B' }}>
                    {selectedTemplate.spriteConfig.cpus}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm" style={{ color: '#6B5D52' }}>Memory</span>
                  <span className="font-medium" style={{ color: '#2F241B' }}>
                    {selectedTemplate.spriteConfig.memoryMb} MB
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm" style={{ color: '#6B5D52' }}>Timeout</span>
                  <span className="font-medium" style={{ color: '#2F241B' }}>
                    {Math.round(selectedTemplate.spriteConfig.timeout / 60000)} minutes
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm" style={{ color: '#6B5D52' }}>Image</span>
                  <code className="text-xs px-2 py-1 rounded" style={{ backgroundColor: '#F5F1EC', color: '#6B5D52' }}>
                    {selectedTemplate.spriteConfig.image}
                  </code>
                </div>
              </div>
            </div>

            {/* Setup Commands */}
            <div className="bg-white rounded-lg p-4 border" style={{ borderColor: '#E8E0D5' }}>
              <h3 className="font-medium mb-3" style={{ color: '#2F241B' }}>
                <Terminal className="w-4 h-4 inline mr-2" />
                Setup Commands
              </h3>
              <div className="space-y-2">
                {selectedTemplate.setupCommands.map((cmd, idx) => (
                  <code
                    key={idx}
                    className="block text-sm px-3 py-2 rounded"
                    style={{ backgroundColor: '#1F2937', color: '#9CA3AF' }}
                  >
                    $ {cmd}
                  </code>
                ))}
              </div>
            </div>

            {/* Execute Command */}
            <div className="bg-white rounded-lg p-4 border" style={{ borderColor: '#E8E0D5' }}>
              <h3 className="font-medium mb-3" style={{ color: '#2F241B' }}>
                <Terminal className="w-4 h-4 inline mr-2" />
                Execute Command
              </h3>
              <code
                className="block text-sm px-3 py-2 rounded"
                style={{ backgroundColor: '#1F2937', color: '#9CA3AF' }}
              >
                $ {selectedTemplate.executeCommand}
              </code>
            </div>

            {/* Cleanup Commands */}
            <div className="bg-white rounded-lg p-4 border" style={{ borderColor: '#E8E0D5' }}>
              <h3 className="font-medium mb-3" style={{ color: '#2F241B' }}>
                <Terminal className="w-4 h-4 inline mr-2" />
                Cleanup Commands
              </h3>
              <div className="space-y-2">
                {selectedTemplate.cleanupCommands.map((cmd, idx) => (
                  <code
                    key={idx}
                    className="block text-sm px-3 py-2 rounded"
                    style={{ backgroundColor: '#1F2937', color: '#9CA3AF' }}
                  >
                    $ {cmd}
                  </code>
                ))}
              </div>
            </div>

            {/* Environment Variables */}
            {Object.keys(selectedTemplate.environmentVariables).length > 0 && (
              <div className="bg-white rounded-lg p-4 border" style={{ borderColor: '#E8E0D5' }}>
                <h3 className="font-medium mb-3" style={{ color: '#2F241B' }}>
                  Environment Variables
                </h3>
                <div className="space-y-2">
                  {Object.entries(selectedTemplate.environmentVariables).map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between">
                      <code className="text-xs font-medium" style={{ color: '#8B5CF6' }}>
                        {key}
                      </code>
                      <code className="text-xs" style={{ color: '#6B5D52' }}>
                        {value}
                      </code>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
